/** `sections/search-callout.liquid` — `<search-callout>`: live tag-based product search. */

const FORM_SELECTOR = '.search-callout-form'
const INPUT_SELECTOR = '.search-callout-input'
const RESULTS_SELECTOR = '[data-search-callout-results]'
const EMPTY_SELECTOR = '[data-search-callout-empty]'
const RESULTS_LIST_SELECTOR = '[data-search-callout-results-list]'
const DEFAULT_SELECTOR = '[data-search-callout-default]'
const DEFAULT_CA_SELECTOR = '[data-search-callout-default-ca]'
const RESULT_SELECTOR = '.search-callout-result'
const PREV_SELECTOR = '[data-search-callout-prev]'
const NEXT_SELECTOR = '[data-search-callout-next]'
const SUGGEST_URL = '/search/suggest'
const SECTION_ID = 'search-callout-results'
const DEFAULT_LIMIT = 6
const MIN_QUERY_LENGTH = 2
const DEBOUNCE_MS = 220

const REGION_LS_KEY = 'wyld_us_state'
const CA_STATE = 'California'

function isCaliforniaVisitor() {
  try {
    return localStorage.getItem(REGION_LS_KEY) === CA_STATE
  } catch {
    return false
  }
}

class SearchCallout extends HTMLElement {
  #abort
  #fetchAbort
  #debounceId

  connectedCallback() {
    this.form = this.querySelector(FORM_SELECTOR)
    this.input = this.querySelector(INPUT_SELECTOR)
    this.resultsEl = this.querySelector(RESULTS_SELECTOR)
    this.emptyEl = this.querySelector(EMPTY_SELECTOR)
    this.prevBtn = this.querySelector(PREV_SELECTOR)
    this.nextBtn = this.querySelector(NEXT_SELECTOR)
    this.limit = Number(this.dataset.limit) || DEFAULT_LIMIT

    if (!this.input || !this.resultsEl) return

    // Cache the server-rendered default products so they can be restored whenever
    // the query is empty. Stored before CA filtering so the filter can be
    // re-applied live when the visitor's region changes. California visitors can
    // get a separate default collection (`data-search-callout-default-ca`); when
    // none is configured the CA state falls back to the standard default list.
    const defaultList = this.resultsEl.querySelector(DEFAULT_SELECTOR)
    const defaultListCa = this.resultsEl.querySelector(DEFAULT_CA_SELECTOR)
    this.defaultMarkup = defaultList ? defaultList.outerHTML : ''
    this.defaultMarkupCa = defaultListCa ? defaultListCa.outerHTML : this.defaultMarkup

    this.#abort = new AbortController()
    const { signal } = this.#abort

    this.input.addEventListener('input', this.#onInput, { signal })
    // Pressing enter submits the form by default, navigating to the search page.
    // Results update live as the visitor types, so suppress that navigation.
    this.form?.addEventListener('submit', this.#onSubmit, { signal })
    // Re-run the current search when the visitor changes region so the CA filter
    // (in #renderResults) is re-applied live. region.js fires this event.
    document.addEventListener('theme:region:state-changed', this.#onRegionChange, { signal })

    // Arrow controls page the horizontally-scrolling results list. The list
    // element is swapped out on every render, so the scroll listener is attached
    // in the capture phase on the stable results container instead.
    this.prevBtn?.addEventListener('click', this.#onPrev, { signal })
    this.nextBtn?.addEventListener('click', this.#onNext, { signal })
    this.resultsEl.addEventListener('scroll', this.#syncArrows, { capture: true, signal })
    globalThis.addEventListener('resize', this.#syncArrows, { signal })

    // Apply the CA filter to the initial default products on load.
    this.#showDefault()
  }

  disconnectedCallback() {
    this.#abort?.abort()
    this.#fetchAbort?.abort()
    clearTimeout(this.#debounceId)
  }

  #onSubmit = (event) => {
    event.preventDefault()
  }

  #onRegionChange = () => {
    const query = this.input?.value.trim()
    if (query && query.length >= MIN_QUERY_LENGTH) {
      this.#search(query)
    } else {
      this.#showDefault()
    }
  }

  #onInput = () => {
    clearTimeout(this.#debounceId)
    const query = this.input.value.trim()

    if (query.length < MIN_QUERY_LENGTH) {
      this.#fetchAbort?.abort()
      this.#showDefault()
      return
    }

    this.#debounceId = setTimeout(() => this.#search(query), DEBOUNCE_MS)
  }

  #showDefault() {
    const markup = isCaliforniaVisitor()
      ? this.defaultMarkupCa
      : this.defaultMarkup
    this.#renderResults(markup, false)
  }

  async #search(query) {
    this.#fetchAbort?.abort()
    this.#fetchAbort = new AbortController()

    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product',
      'resources[limit]': String(this.limit),
      // Match on both product name (title) and tag; predictive search still
      // does prefix matching on the query without the explicit `tag:…*` syntax.
      'resources[options][fields]': 'title,tag',
      section_id: SECTION_ID,
    })

    try {
      const response = await fetch(`${SUGGEST_URL}?${params}`, {
        signal: this.#fetchAbort.signal,
      })
      if (!response.ok) {
        throw new Error(`Search request failed (${response.status})`)
      }
      const markup = await response.text()
      this.#renderResults(markup)
    } catch (error) {
      if (error.name === 'AbortError') return
      this.#renderResults(null)
    }
  }

  #renderResults(markup, isSearch = true) {
    let list = null
    if (markup) {
      const doc = new DOMParser().parseFromString(markup, 'text/html')
      list = doc.querySelector(RESULTS_LIST_SELECTOR)
      // California visitors don't see products unavailable in California.
      // Drop them before the empty-state check so the count stays accurate.
      if (list && isCaliforniaVisitor()) {
        list
          .querySelectorAll(`${RESULT_SELECTOR}[data-ca-available='false']`)
          .forEach((item) => item.remove())
      }
    }

    const hasResults = Boolean(list?.querySelector(RESULT_SELECTOR))

    if (!hasResults) {
      this.resultsEl.replaceChildren()
      this.resultsEl.hidden = true
      // Only show the empty message for an actual search that returned nothing —
      // not for the default (no-query) state.
      if (this.emptyEl) this.emptyEl.hidden = !isSearch
      this.#syncArrows()
      return
    }

    if (this.emptyEl) this.emptyEl.hidden = true
    this.resultsEl.hidden = false
    this.resultsEl.replaceChildren(list)
    this.#syncArrows()
  }

  #currentList() {
    return this.resultsEl?.querySelector(RESULTS_LIST_SELECTOR) ?? null
  }

  #cardStep(list) {
    const cards = list.querySelectorAll(RESULT_SELECTOR)
    if (cards.length < 2) return list.clientWidth
    return cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().left
  }

  #onPrev = () => this.#scrollList(-1)

  #onNext = () => this.#scrollList(1)

  #scrollList(direction) {
    const list = this.#currentList()
    if (!list) return
    const step = this.#cardStep(list)
    // Page by a viewport-width chunk, keeping one card of context between pages.
    const amount = Math.max(step, list.clientWidth - step)
    list.scrollBy({ left: direction * amount, behavior: 'smooth' })
  }

  #syncArrows = () => {
    if (!this.prevBtn || !this.nextBtn) return
    const list = this.resultsEl?.hidden ? null : this.#currentList()
    if (!list) {
      this.prevBtn.disabled = true
      this.nextBtn.disabled = true
      return
    }
    const max = list.scrollWidth - list.clientWidth
    const current = list.scrollLeft
    const hasOverflow = max > 1
    this.prevBtn.disabled = !hasOverflow || current <= 1
    this.nextBtn.disabled = !hasOverflow || current >= max - 1
  }
}

if (!customElements.get('search-callout')) {
  customElements.define('search-callout', SearchCallout)
}
